import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { usePrintAutomation } from "../../../hooks/PrintAutomation/usePrintAutomation"
import type { IPrinterConfigurationDto } from "../../../Objects/PrintAutomation/PrintAutomation"
import type { EPrintType } from "../../../Enum/EPrintType"
import type { IPrintAgentOverviewDto } from "../../../Objects/PrintAutomation/IPrintAgentOverviewDto"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { PrintAutomationHeaderCard } from "./components/PrintAutomationHeaderCard"
import { PrintAgentCard } from "./components/PrintAgentCard"
import { AddPrintAgentPupop } from "./components/AddPrintAgentPupop"
import { PrinterSettingsPopup } from "./components/PrinterSettingsPopup"
import { faAccusoft } from "@fortawesome/free-brands-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"

export const PrintAutomationPage: React.FC = () => {
  const { t } = useTranslation(["pages", "general"])

  const { getOverview, generatePairingCode, testPrint, deleteAgent, savePrinter } = usePrintAutomation()

  const [agents, setAgents] = useState<IPrintAgentOverviewDto[]>([])

  const [pendingTypes, setPendingTypes] = useState<Record<string, EPrintType>>({})

  const [agentName, setAgentName] = useState("Caixa principal")

  const [isAddAgentModalOpen, setIsAddAgentModalOpen] = useState(false)

  const [pairingData, setPairingData] = useState<{ agentId: number; pairingCode: string; expiresAt: string } | null>(
    null,
  )

  const [selectedPrinter, setSelectedPrinter] = useState<IPrinterConfigurationDto | null>(null)

  const totalPrinters = useMemo(() => agents.reduce((acc, agent) => acc + (agent.printers?.length ?? 0), 0), [agents])

  const activePrinters = useMemo(
    () =>
      agents.reduce((acc, agent) => acc + (agent.printers?.filter((printer) => printer.isAgentActive).length ?? 0), 0),
    [agents],
  )

  const onlineAgents = useMemo(() => agents.filter((agent) => agent.isOnline).length, [agents])

  useQuickActionPopup("print-agent-create", () => setIsAddAgentModalOpen(true))

  const load = async () => {
    setAgents(await getOverview())
    setPendingTypes({})
  }

  useEffect(() => {
    void load()
  }, [])

  const handleGeneratePairing = async () => {
    if (!agentName.trim()) return

    const response = await generatePairingCode(agentName)

    setPairingData(response)

    setIsAddAgentModalOpen(false)

    await load()
  }

  const handleDeleteAgent = async (agentId: number) => {
    await deleteAgent(agentId)

    await load()
  }

  const getPairingStatus = (agent: IPrintAgentOverviewDto) => {
    if (!agent.hasPendingPairing) return t("label_print_automation_pairing_active")
    if (!agent.pairingCodeExpiresAt) return t("label_print_automation_pairing_pending")

    return new Date(agent.pairingCodeExpiresAt) < new Date()
      ? t("label_print_automation_pairing_expired")
      : t("label_print_automation_pairing_valid")
  }

  const getPrinterKey = (printer: IPrinterConfigurationDto) =>
    `${printer.id ?? "new"}-${printer.printAgentId}-${printer.printerName}`

  const handleTypeChange = (printer: IPrinterConfigurationDto, printType: EPrintType) => {
    setPendingTypes((previous) => ({ ...previous, [getPrinterKey(printer)]: printType }))
  }

  const handleSaveType = async (printer: IPrinterConfigurationDto, selectedType: EPrintType) => {
    if (!printer.id || !printer.isAgentActive) {
      return
    }

    const nextType = pendingTypes[getPrinterKey(printer)] ?? selectedType ?? printer.printType

    await savePrinter({
      ...printer,
      printType: nextType,
      isEnabled: printer.isEnabled && !!printer.isAgentActive,
    })

    await load()
  }

  return (
    <div className="flex h-[calc(100%-25px)] w-full flex-col overflow-y-auto p-4 md:p-2">
      <PrintAutomationHeaderCard
        totalAgents={agents.length}
        onlineAgents={onlineAgents}
        totalPrinters={totalPrinters}
        activePrinters={activePrinters}
        pairingData={pairingData}
        onOpenAddAgentModal={() => setIsAddAgentModalOpen(true)}
        onRefresh={() => void load()}
      />

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
        <div className="p-2 mt-10">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-100 text-orange-700">
              <FontAwesomeIcon icon={faAccusoft} className="text-sm" />
            </span>

            {t("label_print_automation_agents")}
          </h2>

          {agents.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">{t("label_print_automation_empty")}</p>
          ) : (
            <div className="mt-3 grid gap-3">
              {agents.map((agent) => (
                <PrintAgentCard
                  key={agent.id}
                  agent={agent}
                  pairingStatus={getPairingStatus(agent)}
                  onDelete={(agentId) => void handleDeleteAgent(agentId)}
                  onChangeType={(printer, printType) => void handleTypeChange(printer, printType)}
                  onSaveType={(printer, selectedType) => void handleSaveType(printer, selectedType)}
                  onOpenSettings={(printer) => setSelectedPrinter(printer)}
                  onTestPrint={(printerId) => void testPrint(printerId)}
                />
              ))}
            </div>
          )}
        </div>
      </Animate>

      <AddPrintAgentPupop
        isOpen={isAddAgentModalOpen}
        agentName={agentName}
        onClose={() => setIsAddAgentModalOpen(false)}
        onChangeAgentName={setAgentName}
        onGeneratePairing={() => void handleGeneratePairing()}
      />

      <PrinterSettingsPopup
        printer={selectedPrinter}
        isOpen={!!selectedPrinter}
        onClose={() => setSelectedPrinter(null)}
        onSaved={() => {
          setSelectedPrinter(null)
          void load()
        }}
      />
    </div>
  )
}
